/**
 * WeaveIn.ai Platform - Privacy & Data Shield API Service
 * TypeScript service layer for organization and program privacy policy management
 */

import axios, { AxiosInstance } from 'axios';

import { config as productConfig } from '@/src/config';
// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export type SensitivityTier = 'public' | 'internal' | 'confidential' | 'restricted';

export interface OrgPrivacyPolicy {
  id: number;
  cbo_id: number;
  data_shield_enabled: boolean;
  shield_tier_threshold: SensitivityTier;
  reauth_timeout_minutes: number;
  per_org_encryption_enabled: boolean;
  kms_key_arn: string | null;
  break_glass_enabled: boolean;
  require_access_justification: boolean;
  external_key_exchange_enabled: boolean;
  enhanced_audit_enabled: boolean;
  allow_program_overrides: boolean;
  /** Web sign-out after this many minutes idle (resolved; 480 when unset). Older backends omit it. */
  idle_timeout_minutes?: number;
  /** False when the org has not set a value and the default applies. */
  idle_timeout_configured?: boolean;
}

export interface OrgPrivacyPolicyUpdate {
  data_shield_enabled?: boolean;
  shield_tier_threshold?: SensitivityTier;
  reauth_timeout_minutes?: number;
  per_org_encryption_enabled?: boolean;
  break_glass_enabled?: boolean;
  require_access_justification?: boolean;
  external_key_exchange_enabled?: boolean;
  enhanced_audit_enabled?: boolean;
  allow_program_overrides?: boolean;
  /** 5..720, or null to reset to the platform default (480). */
  idle_timeout_minutes?: number | null;
}

export interface ProgramPrivacyPolicy {
  id: number;
  cbo_id: number;
  program_id: number;
  program_name: string | null;
  data_shield_enabled: boolean | null;
  shield_tier_threshold: SensitivityTier | null;
  reauth_timeout_minutes: number | null;
  require_access_justification: boolean | null;
  enhanced_audit_enabled: boolean | null;
  break_glass_enabled: boolean | null;
  external_key_exchange_enabled: boolean | null;
}

export interface ProgramPrivacyPolicyUpdate {
  data_shield_enabled?: boolean | null;
  shield_tier_threshold?: SensitivityTier | null;
  reauth_timeout_minutes?: number | null;
  require_access_justification?: boolean | null;
  enhanced_audit_enabled?: boolean | null;
  break_glass_enabled?: boolean | null;
  external_key_exchange_enabled?: boolean | null;
}

export interface EffectivePolicy {
  data_shield_enabled: boolean;
  shield_tier_threshold: SensitivityTier;
  reauth_timeout_minutes: number;
  require_access_justification: boolean;
  enhanced_audit_enabled: boolean;
  break_glass_enabled: boolean;
  external_key_exchange_enabled: boolean;
  per_org_encryption_enabled: boolean;
  resolved_from: {
    org_policy: boolean;
    program_ids: number[];
    most_restrictive_program_id: number | null;
  };
}

export interface ProgramPrivacySummaryItem {
  program_id: number;
  program_name: string;
  program_type: string | null;
  is_active: boolean;
  effective_policy: {
    data_shield_enabled: boolean;
    shield_tier_threshold: SensitivityTier;
    reauth_timeout_minutes: number;
    require_access_justification: boolean;
    enhanced_audit_enabled: boolean;
    break_glass_enabled: boolean;
    external_key_exchange_enabled: boolean;
  };
}

// ============================================================================
// FIELD SENSITIVITY TYPES
// ============================================================================

export interface FieldSensitivityDefault {
  field_name: string;
  table_name: string;
  tier_key: SensitivityTier;
  tier_display_name: string;
  is_pii: boolean;
  is_phi: boolean;
  mask_pattern: string | null;
}

export interface FieldSensitivityOverride {
  field_name: string;
  table_name: string;
  sensitivity_tier: SensitivityTier;
  custom_mask_pattern: string | null;
  override_reason: string | null;
}

export interface ProgramFieldSensitivityResponse {
  program_id: number;
  program_name: string | null;
  overrides: FieldSensitivityOverride[];
}

// ============================================================================
// ENCRYPTION KEY MANAGEMENT TYPES
// ============================================================================

export interface EncryptionKeyStatus {
  initialized: boolean;
  kms_key_id: string | null;
  key_version: number;
  algorithm: string;
  status: 'none' | 'active' | 'rotating' | 'revoked';
  last_rotated_at: string | null;
  created_at: string | null;
}

export interface EncryptionInitializeRequest {
  kms_key_arn: string;
}

export interface EncryptionOperationResponse {
  success: boolean;
  message: string;
  key_version: number | null;
  status: string | null;
}

// ============================================================================
// CONSTANTS
// ============================================================================

export const SENSITIVITY_TIERS: { key: SensitivityTier; label: string; description: string; sortOrder: number }[] = [
  { key: 'public', label: 'Public (T1)', description: 'Always visible to authenticated users', sortOrder: 1 },
  { key: 'internal', label: 'Internal (T2)', description: 'Visible to staff, shielded from external', sortOrder: 2 },
  { key: 'confidential', label: 'Confidential (T3)', description: 'Shielded by default, requires re-auth to reveal', sortOrder: 3 },
  { key: 'restricted', label: 'Restricted (T4)', description: 'Shielded + encrypted with per-org key, enhanced audit', sortOrder: 4 },
];

export const TIER_LABELS: Record<SensitivityTier, string> = {
  public: 'Public (T1)',
  internal: 'Internal (T2)',
  confidential: 'Confidential (T3)',
  restricted: 'Restricted (T4)',
};

// ============================================================================
// API CLIENT
// ============================================================================

class PrivacyAPI {
  private client: AxiosInstance;

  constructor(baseURL: string = (productConfig.weaveinApi.baseUrl ?? '').trim()) {
    this.client = axios.create({
      baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add auth token interceptor
    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('auth_token');
      const cboId = localStorage.getItem('active_cbo_id');

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      if (cboId) {
        config.headers['X-CBO-ID'] = cboId;
      }

      return config;
    });
  }

  // ========================================================================
  // ORGANIZATION PRIVACY POLICY
  // ========================================================================

  async getOrgPrivacyPolicy(cboId: number): Promise<OrgPrivacyPolicy> {
    const response = await this.client.get<OrgPrivacyPolicy>(
      `/api/organizations/${cboId}/privacy-policy`
    );
    return response.data;
  }

  async updateOrgPrivacyPolicy(
    cboId: number,
    data: OrgPrivacyPolicyUpdate
  ): Promise<OrgPrivacyPolicy> {
    const response = await this.client.put<OrgPrivacyPolicy>(
      `/api/organizations/${cboId}/privacy-policy`,
      data
    );
    return response.data;
  }

  // ========================================================================
  // PROGRAM PRIVACY POLICY
  // ========================================================================

  async getProgramPrivacyPolicy(
    cboId: number,
    programId: number
  ): Promise<ProgramPrivacyPolicy> {
    const response = await this.client.get<ProgramPrivacyPolicy>(
      `/api/organizations/${cboId}/programs/${programId}/privacy-policy`
    );
    return response.data;
  }

  async updateProgramPrivacyPolicy(
    cboId: number,
    programId: number,
    data: ProgramPrivacyPolicyUpdate
  ): Promise<ProgramPrivacyPolicy> {
    const response = await this.client.put<ProgramPrivacyPolicy>(
      `/api/organizations/${cboId}/programs/${programId}/privacy-policy`,
      data
    );
    return response.data;
  }

  // ========================================================================
  // MEMBER EFFECTIVE PRIVACY
  // ========================================================================

  async getMemberEffectivePrivacy(
    cboId: number,
    memberId: number
  ): Promise<EffectivePolicy> {
    const response = await this.client.get<EffectivePolicy>(
      `/api/organizations/${cboId}/members/${memberId}/effective-privacy`
    );
    return response.data;
  }

  async batchGetMemberEffectivePrivacy(
    cboId: number,
    memberIds: number[]
  ): Promise<Record<number, EffectivePolicy>> {
    const response = await this.client.post<Record<number, EffectivePolicy>>(
      `/api/organizations/${cboId}/members/effective-privacy/batch`,
      { member_ids: memberIds }
    );
    return response.data;
  }

  // ========================================================================
  // PROGRAM PRIVACY SUMMARY
  // ========================================================================

  async getProgramsPrivacySummary(
    cboId: number
  ): Promise<ProgramPrivacySummaryItem[]> {
    const response = await this.client.get<ProgramPrivacySummaryItem[]>(
      `/api/organizations/${cboId}/programs/privacy-summary`
    );
    return response.data;
  }

  // ========================================================================
  // FIELD SENSITIVITY MANAGEMENT
  // ========================================================================

  async getFieldSensitivityDefaults(
    cboId: number,
    tableName: string = 'members'
  ): Promise<FieldSensitivityDefault[]> {
    const response = await this.client.get<FieldSensitivityDefault[]>(
      `/api/organizations/${cboId}/field-sensitivity/defaults`,
      { params: { table_name: tableName } }
    );
    return response.data;
  }

  async getOrgFieldOverrides(
    cboId: number,
    tableName: string = 'members'
  ): Promise<FieldSensitivityOverride[]> {
    const response = await this.client.get<FieldSensitivityOverride[]>(
      `/api/organizations/${cboId}/field-sensitivity/organization`,
      { params: { table_name: tableName } }
    );
    return response.data;
  }

  async setOrgFieldOverrides(
    cboId: number,
    overrides: FieldSensitivityOverride[]
  ): Promise<FieldSensitivityOverride[]> {
    const response = await this.client.put<FieldSensitivityOverride[]>(
      `/api/organizations/${cboId}/field-sensitivity/organization`,
      overrides
    );
    return response.data;
  }

  async getProgramFieldSensitivity(
    cboId: number,
    programId: number,
    tableName: string = 'members'
  ): Promise<ProgramFieldSensitivityResponse> {
    const response = await this.client.get<ProgramFieldSensitivityResponse>(
      `/api/organizations/${cboId}/programs/${programId}/field-sensitivity`,
      { params: { table_name: tableName } }
    );
    return response.data;
  }

  async setProgramFieldSensitivity(
    cboId: number,
    programId: number,
    overrides: FieldSensitivityOverride[]
  ): Promise<ProgramFieldSensitivityResponse> {
    const response = await this.client.put<ProgramFieldSensitivityResponse>(
      `/api/organizations/${cboId}/programs/${programId}/field-sensitivity`,
      overrides
    );
    return response.data;
  }

  // ========================================================================
  // ENCRYPTION KEY MANAGEMENT
  // ========================================================================

  async getEncryptionStatus(cboId: number): Promise<EncryptionKeyStatus> {
    const response = await this.client.get<EncryptionKeyStatus>(
      `/api/organizations/${cboId}/encryption/status`
    );
    return response.data;
  }

  async initializeEncryption(
    cboId: number,
    data: EncryptionInitializeRequest
  ): Promise<EncryptionOperationResponse> {
    const response = await this.client.post<EncryptionOperationResponse>(
      `/api/organizations/${cboId}/encryption/initialize`,
      data
    );
    return response.data;
  }

  async rotateEncryptionKey(cboId: number): Promise<EncryptionOperationResponse> {
    const response = await this.client.post<EncryptionOperationResponse>(
      `/api/organizations/${cboId}/encryption/rotate`
    );
    return response.data;
  }

  async revokeEncryptionKey(cboId: number): Promise<EncryptionOperationResponse> {
    const response = await this.client.delete<EncryptionOperationResponse>(
      `/api/organizations/${cboId}/encryption/revoke`
    );
    return response.data;
  }
}

// Export singleton instance
export const privacyAPI = new PrivacyAPI();
